import React, {useState} from 'react';
import dayjs from "dayjs";
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Row, Col, Input, Select, Button, Popconfirm, Space } from "antd";
import { DeleteOutlined } from '@ant-design/icons';
import { internalApi } from "../../common/axios";
import toast from "react-hot-toast";
import { DIALOG_KEY } from "../../shared/utils/enum";
import MacroEntity from "./MacroEntity";
import { useCountryData } from "../country/useCountryData";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";

dayjs.extend(utc);
dayjs.extend(timezone);
const { Option } = Select;

const CsMacroEditModal = ({record, handleClose, dialogRef, handleSetDialog}) => {
    const data = new MacroEntity(record);
    const [rows, setRows] = useState(data);
    const [macros, setMacros] = useState(data.value || {});
    const [saving, setSaving] = useState(false);
    const { data: countryData, isLoading, error } = useCountryData();
    const langCodes = [...new Set((countryData || []).map(row => row.langCode))];

    const handleSetRows = (key, value) => {
        setRows((prevState) => {
            const updatedData = {...prevState, [key]: value};
            return new MacroEntity(updatedData);
        });
    };

    const handleDelete = async (e) => {
        e.preventDefault();
        const currentDialog = dialogRef.current;
        if (currentDialog.confirmText === 'DELETE') {
            await internalApi.post('cs/macro', {data: rows.toJson(), type: 'delete'}).then((res) => {
                toast.success(res.data.message);
                handleClose(true);
            }).catch((err) => {
                toast.error("삭제 실패");
                console.error(err);
            });
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    };

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        await internalApi.post('cs/macro', {data: rows.toJson(), type: 'save'}).then((res) => {
            toast.success(res.data.message);
            handleClose(true);
        }).catch((err) => {
            toast.error("저장 실패");
            console.error(err);
        }).finally(() => setSaving(false));
        handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
    };

    const handleSetMacro = (key, field, value) => {
        setRows((prevState) => {
            const newMacro = {...prevState};
            newMacro.value[key][field] = value;
            return new MacroEntity(newMacro);
        });
    };

    const handleRemoveMacro = (key) => {
        setRows((prevState) => {
            const newMacro = {...prevState};
            delete newMacro.value[key];
            return new MacroEntity(newMacro);
        });
    };

    const handleAddMacro = (lang) => {
        setRows((prevState) => {
            const newMacro = {...prevState};
            newMacro.value[lang] = { title: '', content: '' };
            return new MacroEntity(newMacro);
        });
    };

    if (isLoading) return <IsLoading />;
    if (error) return <ErrorPage error={error} />;
    return (
        <div className="bannerCreate">
            <Row className="param" style={{ marginBottom: 16 }}>
                <Col span={24}>
                    <Input
                        value={rows.label}
                        placeholder="라벨 이름을 입력하세요(템플릿 답변을 구별 할 수 있는 이름)"
                        size="small"
                        onChange={e => handleSetRows('label', e.target.value)}
                    />
                </Col>
            </Row>
            {Object.keys(rows.value).map((key, index) => (
                <div key={index} style={{ marginBottom: 16, borderBottom: '1px solid #eee', paddingBottom: 12 }}>
                    <Row align="middle" gutter={8} style={{ marginBottom: 8 }}>
                        <Col flex="auto">
                            <Input
                                value={rows.value[key].title}
                                placeholder={`${key} 템플릿 타이틀 입력`}
                                size="small"
                                onChange={e => handleSetMacro(key, 'title', e.target.value)}
                            />
                        </Col>
                        <Col>
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                onClick={() => handleRemoveMacro(key)}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Input.TextArea
                                value={rows.value[key].content}
                                placeholder={`${key} 템플릿 본문 입력`}
                                size="small"
                                autoSize={{ minRows: 4, maxRows: 8 }}
                                onChange={e => handleSetMacro(key, 'content', e.target.value)}
                            />
                        </Col>
                    </Row>
                </div>
            ))}
            <Row className="param" style={{ marginBottom: 16 }}>
                <Col span={24}>
                    <Select
                        placeholder="템플릿 추가"
                        value=""
                        onChange={handleAddMacro}
                        style={{ width: '100%' }}
                        size="small"
                    >
                        {langCodes.map((el) => (
                            <Option
                                key={el}
                                value={el}
                                disabled={rows.value[el]}
                            >
                                {el}
                            </Option>
                        ))}
                    </Select>
                </Col>
            </Row>
            <Row className="buttons" justify="end" gutter={8}>
                <Col>
                    <Button type="primary" loading={saving} onClick={handleSave}>저장</Button>
                </Col>
                <Col>
                    <Button onClick={handleClose}>취소</Button>
                </Col>
                <Col>
                    {rows.index !== 0 && (
                        <Popconfirm
                            title="정말 삭제하시겠습니까? 삭제하려면 'DELETE'를 입력하세요."
                            onConfirm={handleDelete}
                            okText="삭제"
                            cancelText="취소"
                            icon={<DeleteOutlined style={{ color: 'red' }} />}
                            description={<Input onChange={e => handleSetDialog([{ key: DIALOG_KEY.CONFIRM_TEXT, value: e.target.value }])} placeholder="DELETE" />}
                        >
                            <Button danger icon={<DeleteOutlined />}>삭제</Button>
                        </Popconfirm>
                    )}
                </Col>
            </Row>
        </div>
    );
}

export default CsMacroEditModal;
